import { DataStore } from '@aws-amplify/datastore';
import React, { ChangeEvent, useCallback, useState } from 'react';

import { WorkEntry } from '../models';
import { Button, SwitchField, Table, TableBody, TableCell, TableHead, TableRow, TextField } from '@aws-amplify/ui-react';
import { useLoadEntriesForMonth } from './load-entries';
import { asDate } from '../util/date-helpers';

export interface WorkEntriesPerMonthProps {
    month: number;
    year: number;

    onClose: () => unknown;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function convertToDay(inputDate: string) {
    return Number(inputDate.split('-')[2]);
}

function generateDefaultEntry() {
    return {
        day: new Date().getDate(),
        duration: '8',
        isWorkDay: true
    };
}

export const WorkEntriesPerMonth: React.FC<WorkEntriesPerMonthProps> = ({ month: inputMonth, year: inputYear, onClose }) => {
    const [editEntry, setEditEntry] = useState<{ entry: WorkEntry; duration: string; isWorkDay: boolean }>();
    const saveEdit = useCallback(async () => {
        if (editEntry == null) {
            return;
        }

        const entryToSave = WorkEntry.copyOf(editEntry.entry, (e) => {
            e.isWorkDay = editEntry.isWorkDay;
            e.duration = Number(editEntry.duration);
        });
        setEditEntry(undefined);

        return DataStore.save(entryToSave);
    }, [editEntry]);

    const deleteEntry = useCallback((modelId: string) => {
        return DataStore.delete(WorkEntry, modelId);
    }, []);

    const [newEntry, setNewEntry] = useState<{ day: number; duration: string; isWorkDay: boolean }>(generateDefaultEntry);
    const saveEntry = useCallback(() => {
        if (newEntry == null) {
            return Promise.resolve();
        }

        const entryToSave = new WorkEntry({
            date: asDate(inputYear, inputMonth, newEntry.day),
            duration: Number(newEntry.duration),
            isWorkDay: newEntry.isWorkDay
        });
        setNewEntry(generateDefaultEntry());

        return DataStore.save(entryToSave);
    }, [inputMonth, inputYear, newEntry]);

    const workEntries = useLoadEntriesForMonth(inputYear, inputMonth);

    return (
        <>
            <Table caption="" highlightOnHover>
                <TableHead>
                    <TableRow>
                        <TableCell as="th">ID</TableCell>
                        <TableCell as="th">Day</TableCell>
                        <TableCell as="th">Weekday</TableCell>
                        <TableCell as="th">Duration</TableCell>
                        <TableCell as="th">IsWorkDay?</TableCell>
                        <TableCell as="th">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {workEntries?.map((entry) => (
                        <TableRow key={entry.id}>
                            <TableCell>{entry.id}</TableCell>
                            <TableCell>{convertToDay(entry.date)}</TableCell>
                            <TableCell>{WEEKDAYS[new Date(Date.parse(entry.date)).getDay()]}</TableCell>
                            <TableCell>
                                {editEntry?.entry.id === entry.id ? (
                                    <TextField
                                        label="Duration"
                                        labelHidden
                                        variation="quiet"
                                        value={editEntry.duration}
                                        onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                                            setEditEntry((oldEntry) =>
                                                oldEntry == null
                                                    ? oldEntry
                                                    : {
                                                          ...oldEntry,
                                                          duration: evt.target.value
                                                      }
                                            );
                                        }}
                                    />
                                ) : (
                                    entry.duration
                                )}
                            </TableCell>
                            <TableCell>
                                <SwitchField
                                    label=""
                                    isDisabled={editEntry?.entry.id !== entry.id}
                                    isChecked={editEntry?.entry.id !== entry.id ? entry.isWorkDay : editEntry.isWorkDay}
                                    onChange={(evt) => {
                                        setEditEntry((oldEntry) =>
                                            oldEntry == null
                                                ? oldEntry
                                                : {
                                                      ...oldEntry,
                                                      isWorkDay: evt.target.checked
                                                  }
                                        );
                                    }}
                                />
                            </TableCell>
                            <TableCell>
                                {editEntry?.entry.id !== entry.id && (
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            setEditEntry({
                                                entry,
                                                isWorkDay: entry.isWorkDay,
                                                duration: String(entry.duration)
                                            })
                                        }>
                                        💥
                                    </Button>
                                )}
                                {editEntry?.entry.id === entry.id && (
                                    <Button size="small" onClick={saveEdit}>
                                        ✅
                                    </Button>
                                )}
                                <Button size="small" onClick={() => deleteEntry(entry.id)}>
                                    ⛔
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                <TableBody>
                    <TableRow>
                        <TableCell>{'<New Entry>'}</TableCell>
                        <TableCell>
                            <TextField
                                hasError={workEntries?.some((e) => Number(e.date.split('-')[2]) === newEntry.day)}
                                type="number"
                                label="Day"
                                labelHidden
                                variation="quiet"
                                value={newEntry?.day}
                                onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                                    setNewEntry((oldEntry) => ({ ...oldEntry, day: Number(evt.target.value) }));
                                }}
                            />
                        </TableCell>
                        <TableCell>{WEEKDAYS[new Date(Date.parse(asDate(inputYear, inputMonth, newEntry.day))).getDay()]}</TableCell>
                        <TableCell>
                            <TextField
                                label="Duration"
                                labelHidden
                                variation="quiet"
                                value={newEntry?.duration}
                                onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                                    setNewEntry((oldEntry) => ({ ...oldEntry, duration: evt.target.value }));
                                }}
                            />
                        </TableCell>
                        <TableCell>
                            <SwitchField
                                label=""
                                isChecked={newEntry.isWorkDay}
                                onChange={(e) => setNewEntry((oldEntry) => ({ ...oldEntry, isWorkDay: e.target.checked }))}
                            />
                        </TableCell>
                        <TableCell>
                            <Button size="small" onClick={saveEntry}>
                                ✅
                            </Button>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 32 }}>
                <Button size="large" onClick={onClose}>
                    🔙
                </Button>
            </div>
        </>
    );
};
